import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import {
  StatsBand,
  WhyMatters,
  BusinessRisk,
  Audience,
  Capabilities,
  Methodology,
  Deliverables,
  TechStack,
  Resources,
  OpenSourceNote,
  EngagementPaths,
  Eyebrow,
} from "@/components/ServiceSections";
import {
  MatrixTable,
  SpecGrid,
  PhaseTimeline,
  PricingTiers,
  SOCPipeline,
  Certifications,
} from "@/components/TechSections";
import { SERVICES, CERTIFICATIONS } from "@/content/services";
import { RelatedServices } from "@/components/RelatedServices";
import { FaqSection } from "@/components/FaqSection";
import { faqsFor } from "@/content/seo";

const ParentGrid = ({ data }) => (
  <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="solutions-grid">
    <Reveal>
      <Eyebrow>What's Included</Eyebrow>
      <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight">
        Choose your <span className="text-[#8E9AEA]">engagement.</span>
      </h2>
    </Reveal>
    <div className="mt-10 sm:mt-14 grid gap-5 md:grid-cols-2">
      {data.children.map((c, i) => (
        <Reveal key={c.to} delay={i * 0.08}>
          <Link to={c.to} className="glass-card group flex h-full flex-col p-6 sm:p-8" data-testid={`solution-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="font-display text-sm font-bold tracking-[0.3em] text-[#8E9AEA]">0{i + 1}</span>
            <h3 className="mt-4 font-display text-xl font-bold text-white">{c.title}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-[#DDE3F2]/90">{c.copy}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white">
              View service
              <ArrowUpRight className="h-4 w-4 text-[#8E9AEA] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function ServicePage() {
  const { pathname } = useLocation();
  const data = SERVICES[pathname];

  if (!data) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`service-page-${pathname.split("/").pop()}`}>
      <PageHero
        breadcrumb={data.breadcrumb}
        pill={data.pill}
        titleLines={data.titleLines}
        description={data.description}
        primaryCta={data.primaryCta}
        secondaryCta={data.secondaryCta}
        image={data.image}
        complianceItems={data.complianceItems}
      />

      {data.isParent ? (
        <>
          <ParentGrid data={data} />
          <RelatedServices pathname={pathname} />
          <Resources resources={[]} pagePath={pathname} />
          <FaqSection faqs={faqsFor(pathname)} />
          <CTABand />
        </>
      ) : (
        <>
          {data.paths && <EngagementPaths paths={data.paths} />}
          <StatsBand stats={data.stats} />
          {data.ossNote && <OpenSourceNote note={data.ossNote} />}
          <WhyMatters why={data.why} />
          <BusinessRisk risk={data.risk} />
          {data.isProduct && <SOCPipeline />}
          {data.complianceMatrix && <MatrixTable matrix={data.complianceMatrix} />}
          <Audience audience={data.audience} />
          <Capabilities capabilities={data.capabilities} />
          {data.coverage && <MatrixTable matrix={data.coverage} />}
          <Methodology title={data.methodology.title} copy={data.methodology.copy} steps={data.methodology.steps} />
          {data.timeline && <PhaseTimeline timeline={data.timeline} />}
          <Deliverables deliverables={data.deliverables} />
          {data.severity && <MatrixTable matrix={data.severity} />}
          {data.slas && <MatrixTable matrix={data.slas} />}
          {data.spec && <SpecGrid spec={data.spec} />}
          {data.pricing && <PricingTiers pricing={data.pricing} />}
          <TechStack stack={data.stack} />
          {data.showCerts && <Certifications certifications={CERTIFICATIONS} />}
          <Resources resources={data.resources} pagePath={pathname} />
          <RelatedServices pathname={pathname} />
          <FaqSection faqs={faqsFor(pathname)} />
          <CTABand
            title={data.closing.title}
            copy={data.closing.copy}
            primary={{ label: "Get a Consultation", to: "/contact" }}
            secondary={data.closing.secondary}
          />
        </>
      )}
    </div>
  );
}
